import React, { useState } from 'react';
import axios from 'axios';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Search, Package as PackageIcon, MapPin, Phone, Clock } from 'lucide-react';
import PackageStatusBadge from '../../components/PackageStatusBadge';
import CountryDisplay from '../../components/CountryDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { t, language } = useLanguage();
  const [oceCode, setOceCode] = useState('');
  const [packageData, setPackageData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    setError('');
    setPackageData(null);
    setLoading(true);

    try {
      const response = await axios.get(`${API}/packages/track/${oceCode}`);
      setPackageData(response.data);
    } catch (err) {
      setError(t('packageNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const locale = language === 'en' ? 'en-GB' : 'fr-FR';

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PackageIcon className="h-6 w-6" />
              {t('trackYourPackage')}
            </CardTitle>
            <CardDescription>
              {t('enterOceToTrack')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleTrack} className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder={t('enterOcePlaceholder')}
                    value={oceCode}
                    onChange={(e) => setOceCode(e.target.value.toUpperCase())}
                    required
                    className="text-lg"
                  />
                </div>
                <Button type="submit" disabled={loading} size="lg">
                  <Search className="h-5 w-5 mr-2" />
                  {loading ? t('searching') : t('trackBtn')}
                </Button>
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </form>
          </CardContent>
        </Card>

        {packageData && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-2xl">{t('packageDetails')}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Code: {packageData.oce_code}</p>
                </div>
                <PackageStatusBadge status={packageData.status} size="lg" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">{t('sender')}</h3>
                  <p className="text-foreground">{packageData.sender_name}</p>
                  <p className="text-muted-foreground text-sm">{packageData.sender_phone}</p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">{t('recipient')}</h3>
                  <p className="text-foreground">{packageData.recipient_name}</p>
                  <p className="text-muted-foreground text-sm">{packageData.recipient_phone}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h3 className="font-semibold">{t('origin')}</h3>
                  <CountryDisplay country={packageData.origin_country} />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">{t('destination')}</h3>
                  <CountryDisplay country={packageData.destination_country} />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold">{t('description')}</h3>
                <p className="text-foreground">{packageData.description}</p>
                <p className="text-sm text-muted-foreground">{t('weight')}: {packageData.weight} kg</p>
              </div>

              {packageData.delivery_mode === 'relay_point' && packageData.relay_point_info && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                  <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    {t('relayPoint')}
                  </h3>
                  <p className="font-medium text-blue-700">{packageData.relay_point_info.name}</p>
                  <p className="text-sm text-blue-600">{packageData.relay_point_info.address}, {packageData.relay_point_info.city}</p>
                  {packageData.relay_point_info.phone && (
                    <p className="text-sm text-blue-600 flex items-center gap-1">
                      <Phone className="h-4 w-4" /> {packageData.relay_point_info.phone}
                    </p>
                  )}
                  {packageData.relay_point_info.opening_hours && (
                    <p className="text-sm text-blue-600 flex items-center gap-1">
                      <Clock className="h-4 w-4" /> {packageData.relay_point_info.opening_hours}
                    </p>
                  )}
                </div>
              )}

              {packageData.status_history && packageData.status_history.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">{t('trackingHistory')}</h3>
                  <div className="space-y-3">
                    {packageData.status_history.slice().reverse().map((history, index) => (
                      <div key={index} className="flex items-start gap-3 border-l-2 border-accent pl-4 py-2">
                        <div className="flex-1">
                          <PackageStatusBadge status={history.status} size="sm" />
                          <p className="text-sm text-muted-foreground mt-1">
                            {new Date(history.timestamp).toLocaleString(locale)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-accent/10 border-accent/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <PackageIcon className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <h3 className="font-semibold text-accent">{t('aboutOceCode')}</h3>
                <p className="text-sm text-accent mt-1">
                  {t('oceCodeDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ProfessionalLayout>
  );
};

export default ClientDashboard;
